import React, { FC, Fragment, useState, useEffect, useRef } from 'react';

import Loader from 'crehana-ui/Loader';
import LazyImage from 'crehana-ui/LazyImage';

import PlayArrow from 'crehana-icons/PlayArrow';

import { SourceType } from 'crehana-player/types';
import { VIDEO_MIME_TYPES } from 'crehana-player/utils/constants';
import CrehanaGradient from 'crehana-player/components/Gradient';
import PlayerRatio from 'crehana-player/components/Ratio';
import CircularButton from 'crehana-player/components/ui/CircularButton';

import loadScript, { URLS } from 'Jsx/Utils/loadScript';
import {
  trackUserActivity,
  UserActivityEvent,
} from 'Jsx/Analytics/UserActivity';
import Loadable from 'Jsx/Shared/Loadable';

interface TrailerPlayerProps {
  courseId?: number | null;
  title?: string;
  promoVideoHashedId?: string | null;
  promoImage?: string;
  imagePlaceholder?: string;
  trailerSource?: Array<{
    isDefault: boolean;
    videoUrl: string;
    videoSourceType?: string;
    imageUrl?: string;
  }>;
  autoplay?: boolean;
  withUserActivityTrack?: boolean;
}

const TrailerPlayer: FC<TrailerPlayerProps> = ({
  courseId,
  title = '',
  promoVideoHashedId,
  promoImage = '',
  imagePlaceholder,
  trailerSource,
  autoplay,
  withUserActivityTrack,
}) => {
  const [trailerPoster, setTrailerPoster] = useState<string>(promoImage);
  const [crehanaSources, setCrehanaSources] = useState<
    SourceType[] | undefined
  >();
  const [wistiaScriptState, setWistiaScriptState] = useState<
    'skipped' | 'loading' | 'error' | 'loaded'
  >('skipped');

  const videoDuration = useRef<number>(0);
  const progressStartPosition = useRef<number | null>(null);
  const lastTimeUpdate = useRef<{ position: number; date: Date } | null>(null);

  const hasCrehanaSources = trailerSource && trailerSource.length;

  const trackProgress = (progress: number, newStartPosition: number | null) => {
    if (courseId && videoDuration.current && progress > 0 && progress <= 11) {
      trackUserActivity({
        event: UserActivityEvent.PLAY_TRAILER,
        data: {
          product_id: courseId,
          product_name: title,
          duration: videoDuration.current,
          progress,
          item_type: 'course',
        },
      });
    }
    progressStartPosition.current = newStartPosition;
  };

  useEffect(() => {
    return () => {
      // Check if there was a pending progress event that needs to be tracked
      if (
        lastTimeUpdate.current &&
        progressStartPosition.current !== null &&
        lastTimeUpdate.current.position - progressStartPosition.current > 0
      ) {
        trackProgress(
          lastTimeUpdate.current.position - progressStartPosition.current,
          null,
        );
      }
    };
  }, []);

  const onVideoTimeUpdate = (position: number) => {
    if (withUserActivityTrack) {
      const now = new Date();

      // Set the start progress position if none is found
      if (progressStartPosition.current === null) {
        progressStartPosition.current = position;
      }
      // Check if there was a time update before
      if (lastTimeUpdate.current) {
        if (
          // Check if the current position is placed before or more than a second forward from the position on the last time update
          // (This means there was probably a seek event)
          position < lastTimeUpdate.current.position ||
          position - lastTimeUpdate.current.position > 1
        ) {
          // Track event and set the current position as the new progressStart
          trackProgress(
            lastTimeUpdate.current.position - progressStartPosition.current,
            position,
          );
        } else if (
          // Check if the last time update was fired two or more seconds ago
          // (This means the player was paused or buffering)
          now.getTime() - lastTimeUpdate.current.date.getTime() >= 2000 &&
          position - progressStartPosition.current > 0
        ) {
          // Track event and set the progressStartPosition as null so it's restarted on the next time update
          trackProgress(position - progressStartPosition.current, null);
          // Check if the progress duration has reached 10 seconds
        } else if (position - progressStartPosition.current >= 10) {
          trackProgress(position - progressStartPosition.current, null);
        }
      }
      // Set this event data as lastTimeUpdate
      lastTimeUpdate.current = { date: now, position };
    }
  };

  const loadWistiaScript = () => {
    setWistiaScriptState('loading');
    loadScript({ src: URLS.wistia })
      .then(() => {
        setWistiaScriptState('loaded');
        window._wq = window._wq || [];

        window._wq.push({
          id: promoVideoHashedId,
          options: {
            silentAutoPlay: false,
          },
          onReady: player => {
            player.volume(1);
            videoDuration.current = player.duration();
            player.bind('secondchange', s => {
              onVideoTimeUpdate(s);
            });
            if (autoplay) {
              player.play();
            }
          },
        });
      })
      .catch(() => {
        setWistiaScriptState('error');
      });
  };

  useEffect(() => {
    if (trailerSource && trailerSource.length) {
      const list: SourceType[] = [];
      let poster: string | undefined = '';
      let defaultHLS = trailerSource.find(
        source =>
          source.isDefault &&
          source.videoSourceType &&
          source.videoSourceType.toUpperCase().includes('HLS'),
      );

      if (!defaultHLS) {
        defaultHLS = trailerSource.find(
          source =>
            source.videoSourceType &&
            source.videoSourceType.toUpperCase().includes('HLS'),
        );
      }
      if (defaultHLS) {
        list.push({
          type: VIDEO_MIME_TYPES.HLS,
          src: defaultHLS.videoUrl,
        });
        poster = defaultHLS.imageUrl;
      }
      let defaultMP4 = trailerSource.find(
        source =>
          source.isDefault &&
          source.videoSourceType &&
          source.videoSourceType.toUpperCase().includes('MP4'),
      );

      if (!defaultMP4) {
        defaultMP4 = trailerSource.find(
          source =>
            source.videoSourceType &&
            source.videoSourceType.toUpperCase().includes('MP4'),
        );
      }
      if (defaultMP4) {
        list.push({
          type: VIDEO_MIME_TYPES.MP4,
          src: defaultMP4.videoUrl,
        });
        if (!poster) {
          poster = defaultMP4.imageUrl;
        }
      }
      setCrehanaSources(list);
      setTrailerPoster(poster || promoImage);
    } else if (
      promoVideoHashedId &&
      typeof window !== 'undefined' &&
      window.innerWidth >= 768
    ) {
      loadWistiaScript();
    }
  }, [trailerSource, promoVideoHashedId]);

  const renderVideo = () => {
    if (hasCrehanaSources) {
      if (crehanaSources?.length) {
        return (
          <div className="absolute inset-0 w-full h-full">
            <Loadable
              Loader={
                <Fragment>
                  <CrehanaGradient visibleUi />
                  <div className="absolute inset-0 flex items-center justify-center z-2 cursor-pointer">
                    <Loader white />
                  </div>
                </Fragment>
              }
              module={() =>
                import(
                  /* webpackChunkName: "trailer-crehana-player"  */ './CrehanaPlayer'
                )
              }
              onReady={(videoData: { duration: number }) => {
                videoDuration.current = videoData.duration;
              }}
              promoVideoHashedId={promoVideoHashedId}
              crehanaSources={crehanaSources}
              poster={trailerPoster}
              posterPlaceholder={imagePlaceholder}
              onVideoTimeUpdate={onVideoTimeUpdate}
              autoplay={autoplay}
            />
          </div>
        );
      }
      return (
        <Fragment>
          <CrehanaGradient visibleUi />
          <div className="absolute inset-0 flex items-center justify-center z-2 cursor-pointer">
            <div className="absolute inset-0 w-full h-full z-0">
              <LazyImage
                className="w-full h-full"
                imgClassName="object-cover"
                alt={title}
                src={trailerPoster}
                placeholder={imagePlaceholder}
              />
            </div>
            <CircularButton customIcon={PlayArrow} className="z-1" />
          </div>
        </Fragment>
      );
    }
    if (wistiaScriptState === 'skipped' || wistiaScriptState === 'loading') {
      return (
        <button
          type="button"
          className="absolute inset-0 w-full h-full flex items-center justify-center"
          onClick={loadWistiaScript}
        >
          <div className="absolute inset-0 w-full h-full z-0">
            <LazyImage
              className="w-full h-full"
              imgClassName="object-cover"
              alt={title}
              src={trailerPoster}
              placeholder={imagePlaceholder}
            />
          </div>
          <div
            className="absolute inset-0 w-full h-full z-1"
            css={`
              background-color: rgba(0, 0, 0, 0.4);
            `}
          />
          <div
            className="flex items-center justify-center z-2"
            css={`
              background-color: rgba(0, 213, 222, 0.8);
              width: 62.5px;
              height: 40px;
            `}
          >
            <PlayArrow size={20} color="#fff" />
          </div>
        </button>
      );
    }
    return (
      <div
        className={`absolute inset-0 wistia_embed wistia_async_${promoVideoHashedId}`}
      />
    );
  };

  const hasVideo =
    hasCrehanaSources || (promoVideoHashedId && wistiaScriptState !== 'error');

  return (
    <div className="w-full relative">
      {hasVideo ? (
        <Fragment>
          <PlayerRatio className="-z-1" />
          {renderVideo()}
        </Fragment>
      ) : (
        <PlayerRatio poster={promoImage} posterPlaceholder={imagePlaceholder} />
      )}
    </div>
  );
};

export default TrailerPlayer;
